import Vue from 'vue'
import UserCard from '@/components/Users/Card'
import ShippingCheckpoint from '@/components/Shippings/Checkpoint'
import ShippingHistoryListItemId from '@/components/Items/ShippingHistoryListItemId'
import GeofenceTitle from '@/components/Geofences/ItemTitle'

export default {
  computed: {
    itemsModel() {
      return {
        title: 'Items',
        endpoint: this.$appConfig.api.baseUrl + 'items',
        allowNew: false,
        noIdLink: true,
        editInPlace: true,
        listExtrasComponent: () => import('@/components/Items/ListExtras'),
        fields: [
          {
            key: 'id',
            sortable: true,
            editable: false,
            label: this.$gettext('ID'),
            hidden: true
          },
          {
            key: 'reference_code',
            sortable: true,
            editable: false,
            label: this.$gettext('Reference Code')
          },
          {
            key: 'description',
            sortable: true,
            editable: true,
            label: this.$gettext('Description')
          },
          {
            key: 'active',
            sortable: true,
            editable: false,
            label: this.$gettext('Active'),
            type: Boolean,
            class: 'text-center',
            hidden: true
          },
          {
            key: 'size',
            sortable: false,
            editable: false,
            label: this.$gettext('Size'),
            formatter: (value, key, item) => {
              let ret = '<span class="text-nowrap">'
              if (item.width && item.height && item.length) {
                ret += item.width + '×' + item.height + '×' + item.length + ' cm'
                if (item.weight) {
                  ret += ' - '
                }
              }
              if (item.weight) {
                ret += item.weight + ' g'
              }
              ret += '</span>'
              return ret
            },
            hidden: true
          },
          {
            key: 'width',
            label: this.$gettext('Width'),
            hidden: true
          },
          {
            key: 'height',
            label: this.$gettext('Height'),
            hidden: true
          },
          {
            key: 'length',
            label: this.$gettext('Length'),
            hidden: true
          },
          {
            key: 'weight',
            label: this.$gettext('Weight'),
            hidden: true
          },
          {
            key: 'price',
            label: this.$gettext('Price'),
            hidden: true
          },
          {
            key: 'serial_number',
            sortable: false,
            editable: true,
            label: this.$gettext('Serial Number'),
            formatter: (value, key, item) => {
              return value?.join(', ')
            },
            hidden: true
          },
          {
            key: 'quantity',
            sortable: false,
            editable: false,
            label: this.$gettext('Quantity'),
            class: 'text-center',
            hidden: true
          },
          {
            key: 'last_position_updated_at',
            sortable: true,
            editable: false,
            label: this.$gettext('Last Updated Position'),
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            },
            hidden: true
          },
          {
            key: 'created_at',
            sortable: true,
            editable: false,
            label: this.$gettext('Created At'),
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            },
            hidden: true
          },
          {
            key: 'updated_at',
            sortable: true,
            editable: false,
            label: this.$gettext('Last Updated'),
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            },
            hidden: true
          }
        ],
        extraActions: [
          {
            label: '',
            type: () => import('@/components/Items/ListItemActions'), // external bunch of actions
            position: 'start',
            props: (item) => {
              return {
                item: item,
                type: 'items'
              }
            }
          },
          {
            type: 'b-dropdown-divider',
            position: 'start'
          }
        ],
        filters: [
          'id',
          'reference_code',
          'description',
          'serial_number',
          'active',
          'last_position_updated_at'
        ],
        includes: [
          'item_tracking',
          'shipping.items',
          'location',
          'last_known_location'
        ],
        specificFields: 'id,reference_code,description,active,created_at,updated_at,currency,width,height,length,weight,price,quantity,item_tracking,last_known_location_model_id,last_known_location_model_type,last_position_updated_at,location_model_id,location_model_type,serial_number,shipping,meta',
        exclude: [
          'width',
          'height',
          'length',
          'weight'
        ]
      }
    },
    itemsShippingHistoryModel() {
      return {
        title: 'Items Shipping History',
        endpoint: this.$appConfig.api.baseUrl + 'shippings',
        listExtrasComponent: () => import('@/components/Items/ShippingHistoryListExtras'),
        allowNew: false,
        allowSelect: true,
        fields: [
          {
            key: 'id',
            sortable: false,
            editable: false,
            label: this.$gettext('ID'),
            component: (value, key, item) => {
              return {
                is: ShippingHistoryListItemId,
                item: item
              }
            }
          },
          {
            key: 'from_contact',
            sortable: false,
            label: this.$gettext('Origin'),
            class: 'col-contact',
            component: (value, key, item) => {
              return {
                is: ShippingCheckpoint,
                shipping: item,
                checkpoint: 'from',
                withEdit: !this.ecosystemConfig.disableEditOrigins,
                withIcons: true,
                withTooltip: true
              }
            }
          },
          {
            key: 'to_contact',
            sortable: false,
            label: this.$gettext('Destination'),
            class: 'col-contact',
            component: (value, key, item) => {
              return {
                is: ShippingCheckpoint,
                shipping: item,
                checkpoint: 'to',
                withEdit: !this.ecosystemConfig.disableEditDestinations,
                withIcons: true,
                withTooltip: true
              }
            }
          },
          {
            key: 'status',
            sortable: true,
            label: this.$gettext('Status')
          },
          {
            key: 'courier',
            sortable: false,
            label: this.$gettext('Courier'),
            component: (value, key, item) => {
              return {
                is: 'user-card',
                user: item.courier || {},
                profilePicture: false,
                withTitles: false
              }
            }
          },
          {
            key: 'created_at',
            sortable: true,
            editable: false,
            label: this.$gettext('Created At'),
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          },
          {
            key: 'updated_at',
            sortable: true,
            editable: false,
            label: this.$gettext('Updated At'),
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          }
        ],
        includes: [
          'courier.courier_available.tracking',
          'courier.roles',
          'courier.company',
          'status_history',
          'pending_hooks',
          'previousShipping',
          'followingShippings',
          'items'
        ],
        filters: [
          'items.id',
          'items.reference_code'
        ],
        exclude: []
      }
    },
    itemsFromGeofenceModel() {
      return {
        title: 'Items From Geofence History',
        endpoint: null,
        allowNew: false,
        allowSelect: false,
        fields: [
          {
            key: 'reference_code',
            sortable: false,
            editable: false,
            label: this.$gettext('Reference Code')
          },
          {
            key: 'location',
            sortable: false,
            label: this.$gettext('In Geofence'),
            component: (value, key, item) => {
              return {
                is: GeofenceTitle,
                geofence: value
              }
            }
          },
          {
            key: 'distance',
            sortable: false,
            label: this.$gettext('Distance'),
            formatter: (value, key, item) => {
              return this.$options.filters.distance(value, {unitSystem: this.frontConfig.i18n.unitSystem, locale: this.frontConfig.i18n.moment})
            }
          },
          {
            key: 'idle',
            label: this.$gettext('Idle Status'),
            formatter: (value, key, item) => {
              let itemColor
              if (this.ecosystemModules.items.icon.colorByMinAge) {
                const daysIdle = this.elapsedTimeRaw(item.last_position_updated_at) / 86400000
                itemColor = this.itemColorByAge(daysIdle, 'items')
              }
              let lastUpdatedDate = this.$options.filters.tabledatetime(item.last_position_updated_at)
              let output = `<div style="width: 1em; height: 1em; border-radius: 1em; background-color: ${itemColor || '#333'};"`
              if (lastUpdatedDate !== 'Invalid date') {
                output += 'title="' + this.$gettextInterpolate(this.$gettext('Position Last Update: %{lastUpdatedDate}'), {lastUpdatedDate: lastUpdatedDate.replace('<br>', ' ')}) + '"'
              }
              output += `></div>`
              return output
            }
          },
          {
            key: 'shipping',
            label: this.$gettext('In Shipping'),
            component: (value, key, item) => {
              return {
                is: ShippingHistoryListItemId,
                item: value || {id: ''}
              }
            }
          }
        ],
        includes: [],
        filters: [],
        exclude: []
      }
    },
    itemsTrackingHistoryEventsModel() {
      return {
        title: 'Items Tracking History Events',
        endpoint: this.$appConfig.api.baseUrl + 'items-tracking/history/events',
        listExtrasComponent: () => import('@/components/Items/TrackingHistoryEventsListExtras'),
        allowNew: false,
        allowSelect: false,
        noActions: true,
        fields: [
          {
            key: 'created_at',
            sortable: true,
            editable: false,
            label: this.$gettext('Event Date'),
            formatter: (value, key, item) => {
              return this.$options.filters.tabledatetime(value, this.$tz, this.frontConfig.i18n.moment)
            }
          },
          {
            key: 'item_id',
            sortable: false,
            label: this.$gettext('Event'),
            class: 'text-center',
            formatter: (value, key, item) => {
              let html = '<div class="d-flex">'
              if (item.event_name === 'entry') {
                html += '<div class="flex-fill h4 text-right mr-2"><i class="fas fa-arrow-circle-right"></i><i class="fas fa-arrow-circle-right"></i><i class="fas fa-arrow-circle-right"></i></div>'
                html += `<div class="flex-fill text-left"><strong>${item.owner.external_id}</strong> ${item.owner.name}</div>`
                html += '<div class="flex-fill h4 text-center">&nbsp;&nbsp;&nbsp;</div>'
              } else if (item.event_name === 'exit') {
                html += '<div class="flex-fill h4 text-center">&nbsp;&nbsp;&nbsp;</div>'
                html += `<div class="flex-fill text-right"><strong>${item.owner.external_id}</strong> ${item.owner.name}</div>`
                html += '<div class="flex-fill h4 text-left ml-2"><i class="fas fa-arrow-circle-right"></i><i class="fas fa-arrow-circle-right"></i><i class="fas fa-arrow-circle-right"></i></div>'
              } else if (item.event_name === 'item_pickup') {
                const userCard = new Vue({
                  ...UserCard,
                  parent: this,
                  propsData: {
                    user: item.applied_by,
                    profilePicture: false,
                    cardStyle: 'micro'
                  }
                }).$mount().$el.outerHTML
                html += '<div class="flex-fill h4 text-right"><i class="fas fa-arrow-circle-up"></i><i class="fas fa-arrow-circle-up"></i><i class="fas fa-arrow-circle-up"></i></div>'
                html += `<div class="flex-fill text-center">${userCard}</div>`
                html += '<div class="flex-fill h4 text-left ml-2"><i class="fas fa-arrow-circle-up"></i><i class="fas fa-arrow-circle-up"></i><i class="fas fa-arrow-circle-up"></i></div>'
              } else if (item.event_name === 'item_dropoff') {
                const userCard = new Vue({
                  ...UserCard,
                  parent: this,
                  propsData: {
                    user: item.applied_by,
                    profilePicture: false,
                    cardStyle: 'micro'
                  }
                }).$mount().$el.outerHTML
                html += '<div class="flex-fill h4 text-right"><i class="fas fa-arrow-circle-down"></i><i class="fas fa-arrow-circle-down"></i><i class="fas fa-arrow-circle-down"></i></div>'
                html += `<div class="flex-fill text-center">${userCard}</div>`
                html += '<div class="flex-fill h4 text-left ml-2"><i class="fas fa-arrow-circle-down"></i><i class="fas fa-arrow-circle-down"></i><i class="fas fa-arrow-circle-down"></i></div>'
              }
              html += '</div>'
              return html
            }
          }
        ],
        includes: [
          'applied_by',
          'owner'
        ],
        filters: [],
        exclude: []
      }
    }
  }
}
